import {useState} from 'react';
import {Box, IconButton, Typography} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import CancelOutlinedIcon from '@mui/icons-material/CancelOutlined';
import {t} from 'i18next';
import ArrSectionCard, {
  ArrSectionCardProps,
} from '../../../../common/arr-accordion-v2/ArrSectionCard';
import ArrTextField from '../../../../common/arr-text-field/ArrTextField';
import ArrButton from '../../../../common/arr-button/ArrButton';
import AutoGrowTextField from '../auto-grow-text-field';
import {ReactComponent as DeleteIcon} from '../../../../../assets/images/close-base-icon.svg';
import {
  CaseContext,
  PartyRole,
} from '../../../../../redux/slice/aiReviewSliceV2';
import {
  CASE_CONTEXT_RESPONSIVENESS_EXAMPLE_KEYS,
  CASE_CONTEXT_RESPONSIVENESS_SUBSECTION_KEYS,
  CaseContextFieldConfig,
  EMPTY_PARTY_ROLE,
} from './caseContextConfig';
import {MOCK_CASE_CONTEXT_VIEW_BANNERS} from './caseContextMockData';
import CaseContextViewSectionCard from './CaseContextViewSectionCard';

export type CaseContextMode = 'edit' | 'view';

interface CaseContextFieldCardProps {
  config: CaseContextFieldConfig;
  caseContext: CaseContext;
  mode: CaseContextMode;
  onUpdate: (partial: Partial<CaseContext>) => void;
}

const FIELD_CARD_CLASS = 'case-context-field-card';
const CASE_CONTEXT_FIELD_CLASS = 'case-context-field-card__field';
const VIEW_BODY_CLASS = 'case-context-view-section-card__view-body';
const VIEW_EMPTY_CLASS = 'case-context-view-section-card__empty';

const CASE_CONTEXT_SINGLE_LINE_FIELD_PROPS = {
  state: 'enabled' as const,
  sizeVariant: 'medium' as const,
};

const isPartyFilled = (party: PartyRole) =>
  Boolean(
    party.party?.trim() ||
      party.role?.trim() ||
      party?.party_description?.trim()
  );

/** Parties & Roles keep default (filled) body; all other sections are transparent. */
const getBodyVariant = (
  config: CaseContextFieldConfig
): 'default' | 'transparent' =>
  config.type === 'parties' ? 'default' : 'transparent';

const CaseContextFieldCard = ({
  config,
  caseContext,
  mode,
  onUpdate,
}: CaseContextFieldCardProps) => {
  const [keywordDraft, setKeywordDraft] = useState('');
  const baseLabel = t(config.labelKey);
  const emptyDisplay = t('questionReadOnlyList.none');
  const label = (
    <span>
      {baseLabel}
      {config.required && mode === 'edit' && (
        <span
          className="case-context-field-card__required-marker typo-text subhead4_size"
          aria-label={t('common.requiredField')}
        >
          *
        </span>
      )}
    </span>
  );
  const placeholder = config.placeholderKey ? t(config.placeholderKey) : '';
  const subLabel = config.subLabelKey ? t(config.subLabelKey) : '';
  const isViewMode = mode === 'view';
  const dataTestId = `case-context-field-${config.key}`;
  const isResponsivenessSubsection =
    CASE_CONTEXT_RESPONSIVENESS_SUBSECTION_KEYS.includes(config.key);
  const addItemPlaceholder = t(
    'ai.review.protocolWizard.caseContext.placeholders.addItem'
  );
  const bodyVariant = getBodyVariant(config);
  const bannerContent = isViewMode
    ? MOCK_CASE_CONTEXT_VIEW_BANNERS[config.key]
    : undefined;

  const isResponsivenessExamplesField =
    CASE_CONTEXT_RESPONSIVENESS_EXAMPLE_KEYS.includes(config.key);

  const fieldCardClassName = `${FIELD_CARD_CLASS}${
    isResponsivenessSubsection
      ? ' case-context-field-card--responsiveness-subsection'
      : ''
  }${
    isResponsivenessExamplesField
      ? ' case-context-field-card--responsiveness-examples'
      : ''
  }`;

  const infoContent = config.tooltipKey ? t(config.tooltipKey) : undefined;
  const showInfoIcon = Boolean(config.tooltipKey);

  const editSectionCardProps: Pick<
    ArrSectionCardProps,
    | 'accordion'
    | 'showInfoIcon'
    | 'infoContent'
    | 'className'
    | 'dataTestId'
    | 'bodyVariant'
  > = {
    accordion: true,
    showInfoIcon,
    infoContent,
    className: fieldCardClassName,
    dataTestId,
    bodyVariant,
  };

  const viewSectionCardProps = {
    accordion: true as const,
    showInfoIcon,
    infoContent,
    className: fieldCardClassName,
    dataTestId,
    bodyVariant,
    bannerContent,
    viewAsList: config.type === 'stringList',
  };

  const renderSublabel = () =>
    subLabel ? (
      <Typography
        className="case-context-field-card__sublabel"
        data-testid={`${dataTestId}-sub-label`}
      >
        {subLabel}
      </Typography>
    ) : null;

  const renderGroupSubheading = (label: string, testId: string) => (
    <Typography
      className="case-context-field-card__group-subheading typo-text label2_size"
      data-testid={testId}
    >
      {label}
    </Typography>
  );

  const renderViewText = (value: string) => {
    const trimmed = value?.trim();
    return (
      <Typography
        className={trimmed ? VIEW_BODY_CLASS : VIEW_EMPTY_CLASS}
        data-testid={`${dataTestId}-view`}
      >
        {trimmed || emptyDisplay}
      </Typography>
    );
  };

  const renderEmptyText = (testId: string) => (
    <Typography className={VIEW_EMPTY_CLASS} data-testid={testId}>
      {emptyDisplay}
    </Typography>
  );

  const renderKeywordChips = (
    keywords: string[],
    onRemove?: (index: number) => void
  ) => {
    const displayKeywords = isViewMode
      ? keywords.filter((keyword) => keyword.trim().length > 0)
      : keywords;

    if (displayKeywords.length === 0) {
      return isViewMode ? renderViewText('') : null;
    }

    return (
      <Box
        className="case-context-keyword-chips"
        data-testid={`${dataTestId}-chips`}
      >
        {displayKeywords.map((keyword, index) => (
          <Box
            key={`${keyword}-${index}`}
            className="case-context-keyword-chip"
            data-testid={`${dataTestId}-chip-${index}`}
          >
            <Typography
              className="case-context-keyword-chip__label"
              component="span"
            >
              {keyword}
            </Typography>
            {onRemove && (
              <IconButton
                size="small"
                className="case-context-keyword-chip__close"
                aria-label={t('common.remove')}
                data-testid={`${dataTestId}-chip-remove-${index}`}
                disableRipple
                onClick={() => onRemove(index)}
              >
                <CancelOutlinedIcon />
              </IconButton>
            )}
          </Box>
        ))}
      </Box>
    );
  };

  if (config.type === 'relevantTimePeriods') {
    const primaryDateRange = caseContext.primary_date_range;
    const subperiods = caseContext.important_subperiods;
    const displaySubperiods = subperiods.length > 0 ? subperiods : [''];
    const showSubperiodDelete =
      displaySubperiods.length > 1 ||
      displaySubperiods.some((item) => item.trim().length > 0);
    const primaryLabel = t(
      'ai.review.protocolWizard.caseContext.fields.primaryDateRange'
    );
    const subperiodsLabel = t(
      'ai.review.protocolWizard.caseContext.fields.importantSubperiods'
    );

    const handleAddSubperiod = () => {
      const base = subperiods.length > 0 ? subperiods : [''];
      onUpdate({important_subperiods: [...base, '']});
    };

    const handleDeleteSubperiod = (index: number) => {
      const next = displaySubperiods.filter((_, i) => i !== index);
      onUpdate({
        important_subperiods: next.length > 0 ? next : [],
      });
    };

    const updateSubperiod = (index: number, value: string) => {
      const base = subperiods.length > 0 ? subperiods : [''];
      onUpdate({
        important_subperiods: base.map((item, i) =>
          i === index ? value : item
        ),
      });
    };

    const viewSubperiods = subperiods.filter((item) => item.trim().length > 0);

    const renderRelevantTimePeriodsBody = () => (
      <Box
        className="case-context-relevant-time-periods"
        data-testid={`${dataTestId}-body`}
      >
        {renderGroupSubheading(
          primaryLabel,
          `${dataTestId}-primary-date-range-label`
        )}
        {isViewMode ? (
          primaryDateRange?.trim() ? (
            <Typography
              className={VIEW_BODY_CLASS}
              data-testid={`${dataTestId}-primary-date-range-view`}
            >
              {primaryDateRange}
            </Typography>
          ) : (
            renderEmptyText(`${dataTestId}-primary-date-range-view`)
          )
        ) : (
          <AutoGrowTextField
            className={CASE_CONTEXT_FIELD_CLASS}
            multiline
            value={primaryDateRange}
            placeholder={placeholder || addItemPlaceholder}
            dataTestId={`${dataTestId}-primary-date-range-input`}
            onChange={(value) => onUpdate({primary_date_range: value})}
          />
        )}

        {renderGroupSubheading(
          subperiodsLabel,
          `${dataTestId}-important-subperiods-label`
        )}
        <Box
          className="case-context-relevant-time-periods__subperiods"
          data-testid={`${dataTestId}-important-subperiods`}
        >
          {isViewMode
            ? viewSubperiods.length === 0
              ? renderEmptyText(`${dataTestId}-important-subperiods-view`)
              : viewSubperiods.map((item, index) => (
                  <Typography
                    key={`subperiod-view-${index}`}
                    className={VIEW_BODY_CLASS}
                    data-testid={`${dataTestId}-important-subperiods-view-${index}`}
                  >
                    {item}
                  </Typography>
                ))
            : displaySubperiods.map((item, index) => (
                <div
                  key={`subperiod-${index}`}
                  className="arr-section-card__item"
                  data-testid={`${dataTestId}-important-subperiods-item-${index}`}
                >
                  <div className="arr-section-card__item-content">
                    <AutoGrowTextField
                      className={CASE_CONTEXT_FIELD_CLASS}
                      multiline
                      value={item}
                      placeholder={placeholder || addItemPlaceholder}
                      dataTestId={`${dataTestId}-important-subperiods-input-${index}`}
                      onChange={(value) => updateSubperiod(index, value)}
                    />
                  </div>
                  {showSubperiodDelete && (
                    <IconButton
                      className="arr-section-card__delete-btn"
                      onClick={() => handleDeleteSubperiod(index)}
                      size="small"
                      aria-label={t('common.delete')}
                      data-testid={`${dataTestId}-important-subperiods-delete-${index}`}
                    >
                      <DeleteIcon aria-hidden="true" />
                    </IconButton>
                  )}
                </div>
              ))}
        </Box>
        {!isViewMode && (
          <div className="arr-section-card__footer">
            <ArrButton
              variant="text"
              size="medium"
              startIcon={<AddIcon />}
              className="arr-section-card__add-btn"
              dataTestId={`${dataTestId}-important-subperiods-add`}
              onClick={handleAddSubperiod}
            >
              {t('common.add')}
            </ArrButton>
          </div>
        )}
      </Box>
    );

    if (isViewMode) {
      return (
        <CaseContextViewSectionCard
          {...viewSectionCardProps}
          label={label}
          items={['view']}
          renderItem={() => renderRelevantTimePeriodsBody()}
        />
      );
    }

    return (
      <ArrSectionCard
        {...editSectionCardProps}
        label={label}
        items={['content']}
        showAddButton={false}
        showDeleteButton={false}
        renderItem={() => renderRelevantTimePeriodsBody()}
      />
    );
  }

  if (config.type === 'longText' || config.type === 'singleText') {
    const value = caseContext[config.key] as string;

    if (isViewMode) {
      return (
        <CaseContextViewSectionCard
          {...viewSectionCardProps}
          label={label}
          items={[value]}
          renderItem={(item: string) => (
            <>
              {renderSublabel()}
              {renderViewText(item)}
            </>
          )}
        />
      );
    }

    // Placeholder-only: hides automatically when typing (no floating-label overlap)
    return (
      <ArrSectionCard
        {...editSectionCardProps}
        label={label}
        items={[value]}
        showAddButton={false}
        showDeleteButton={false}
        renderItem={() => (
          <>
            {renderSublabel()}
            <AutoGrowTextField
              className={CASE_CONTEXT_FIELD_CLASS}
              multiline
              value={value}
              placeholder={placeholder || baseLabel}
              dataTestId={`${dataTestId}-input`}
              onChange={(nextValue) => onUpdate({[config.key]: nextValue})}
            />
          </>
        )}
      />
    );
  }

  if (config.type === 'keywordChips') {
    const keywords = caseContext[config.key] as string[];

    const commitKeyword = () => {
      const next = keywordDraft.trim();
      if (!next) return;
      onUpdate({[config.key]: [...keywords, next]});
      setKeywordDraft('');
    };

    const removeKeyword = (index: number) => {
      onUpdate({
        [config.key]: keywords.filter((_, i) => i !== index),
      });
    };

    if (isViewMode) {
      return (
        <CaseContextViewSectionCard
          {...viewSectionCardProps}
          label={label}
          items={['view']}
          renderItem={() => renderKeywordChips(keywords)}
        />
      );
    }

    return (
      <ArrSectionCard
        {...editSectionCardProps}
        label={label}
        items={['composer']}
        showAddButton={false}
        showDeleteButton={false}
        renderItem={() => (
          <Box className="case-context-keyword-field">
            <Box className="case-context-keyword-field__composer">
              <ArrTextField
                {...CASE_CONTEXT_SINGLE_LINE_FIELD_PROPS}
                fullWidth
                className={CASE_CONTEXT_FIELD_CLASS}
                value={keywordDraft}
                placeholder={placeholder || addItemPlaceholder}
                dataTestId={`${dataTestId}-input`}
                onChange={(e) => setKeywordDraft(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    commitKeyword();
                  }
                }}
              />
              <ArrButton
                variant="text"
                size="medium"
                startIcon={<AddIcon />}
                className="arr-section-card__add-btn case-context-keyword-field__add"
                dataTestId={`${dataTestId}-add`}
                disabled={!keywordDraft.trim()}
                onClick={commitKeyword}
              >
                {t('common.add')}
              </ArrButton>
            </Box>
            {renderKeywordChips(keywords, removeKeyword)}
          </Box>
        )}
      />
    );
  }

  if (config.type === 'parties') {
    const parties = caseContext.parties_and_roles;
    // Empty state: always one blank row + Add button
    const displayParties =
      parties.length > 0 ? parties : [{...EMPTY_PARTY_ROLE}];
    const hasPartyContent = displayParties.some(isPartyFilled);

    if (isViewMode) {
      const viewParties = parties.filter(isPartyFilled);

      return (
        <CaseContextViewSectionCard
          {...viewSectionCardProps}
          label={label}
          items={viewParties.length > 0 ? viewParties : [EMPTY_PARTY_ROLE]}
          renderItem={(party: PartyRole) => (
            <Box className="case-context-view-section-card__party-view">
              <Typography
                className="case-context-view-section-card__party-heading"
                component="div"
                align="left"
              >
                <span
                  className={
                    party.party?.trim()
                      ? 'case-context-view-section-card__party-name'
                      : VIEW_EMPTY_CLASS
                  }
                >
                  {party.party?.trim() ? party.party : emptyDisplay}
                </span>
                <span
                  className={
                    party.role?.trim()
                      ? 'case-context-view-section-card__party-role'
                      : VIEW_EMPTY_CLASS
                  }
                >
                  {party.role?.trim() ? `· ${party.role}` : emptyDisplay}
                </span>
              </Typography>
              <Typography
                className={
                  party.party_description?.trim()
                    ? 'case-context-view-section-card__party-description'
                    : VIEW_EMPTY_CLASS
                }
                align="left"
                component="p"
              >
                {party.party_description?.trim()
                  ? party.party_description
                  : emptyDisplay}
              </Typography>
            </Box>
          )}
        />
      );
    }

    const handleAddParty = () => {
      const base = parties.length > 0 ? parties : [{...EMPTY_PARTY_ROLE}];
      onUpdate({
        parties_and_roles: [...base, {...EMPTY_PARTY_ROLE}],
      });
    };

    const handleDeleteParty = (index: number) => {
      const next = displayParties.filter((_, i) => i !== index);
      onUpdate({
        parties_and_roles: next.length > 0 ? next : [],
      });
    };

    const updateParty = (
      index: number,
      field: keyof PartyRole,
      value: string
    ) => {
      const base = parties.length > 0 ? parties : [{...EMPTY_PARTY_ROLE}];
      onUpdate({
        parties_and_roles: base.map((party, i) =>
          i === index ? {...party, [field]: value} : party
        ),
      });
    };

    return (
      <ArrSectionCard
        {...editSectionCardProps}
        label={label}
        items={displayParties}
        showAddButton
        showDeleteButton={displayParties.length > 1 || hasPartyContent}
        onAdd={handleAddParty}
        onDelete={handleDeleteParty}
        renderItem={(party: PartyRole, index: number) => (
          <>
            <ArrSectionCard.Row>
              <ArrSectionCard.Item flex={2}>
                <ArrTextField
                  className={CASE_CONTEXT_FIELD_CLASS}
                  value={party.party}
                  placeholder={t(config.partyFieldKeys!.party)}
                  dataTestId={`${dataTestId}-party-${index}`}
                  onChange={(value) =>
                    updateParty(index, 'party', value.target.value)
                  }
                />
              </ArrSectionCard.Item>
              <ArrSectionCard.Item flex={1}>
                <ArrTextField
                  className={CASE_CONTEXT_FIELD_CLASS}
                  value={party.role}
                  placeholder={t(config.partyFieldKeys!.role)}
                  dataTestId={`${dataTestId}-role-${index}`}
                  onChange={(value) =>
                    updateParty(index, 'role', value.target.value)
                  }
                />
              </ArrSectionCard.Item>
            </ArrSectionCard.Row>
            <ArrSectionCard.Row>
              <ArrSectionCard.Item flex={1}>
                <AutoGrowTextField
                  className={CASE_CONTEXT_FIELD_CLASS}
                  multiline
                  value={party.party_description}
                  placeholder={t(config.partyFieldKeys!.partyDescription)}
                  dataTestId={`${dataTestId}-description-${index}`}
                  onChange={(value) =>
                    updateParty(index, 'party_description', value)
                  }
                />
              </ArrSectionCard.Item>
            </ArrSectionCard.Row>
          </>
        )}
      />
    );
  }

  const listValue = caseContext[config.key] as string[];
  // Empty state: always one blank field + Add button
  const displayItems = listValue.length > 0 ? listValue : [''];
  const hasListContent = displayItems.some((item) => item.trim().length > 0);

  if (isViewMode) {
    const viewItems = listValue.filter((item) => item.trim().length > 0);

    return (
      <CaseContextViewSectionCard
        {...viewSectionCardProps}
        label={label}
        items={viewItems.length > 0 ? viewItems : ['']}
        viewAsList={viewItems.length > 0}
        renderItem={(item: string) => renderViewText(item)}
      />
    );
  }

  const handleAddItem = () => {
    const base = listValue.length > 0 ? listValue : [''];
    onUpdate({[config.key]: [...base, '']});
  };

  const handleDeleteItem = (index: number) => {
    const next = displayItems.filter((_, i) => i !== index);
    onUpdate({
      [config.key]: next.length > 0 ? next : [],
    });
  };

  const updateItem = (index: number, value: string) => {
    const base = listValue.length > 0 ? listValue : [''];
    onUpdate({
      [config.key]: base.map((item, i) => (i === index ? value : item)),
    });
  };

  return (
    <ArrSectionCard
      {...editSectionCardProps}
      label={label}
      items={displayItems}
      showAddButton
      showDeleteButton={displayItems.length > 1 || hasListContent}
      onAdd={handleAddItem}
      onDelete={handleDeleteItem}
      renderItem={(item: string, index: number) => (
        <AutoGrowTextField
          className={CASE_CONTEXT_FIELD_CLASS}
          multiline
          value={item}
          placeholder={placeholder || addItemPlaceholder}
          dataTestId={`${dataTestId}-input-${index}`}
          onChange={(value) => updateItem(index, value)}
        />
      )}
    />
  );
};

export default CaseContextFieldCard;
